import { useEffect, useState } from "react";
import { apiErrorText, classifyLoginError } from "@core/messages";
import type { OtpStep } from "@core/api";
import { parseAssemblyLink, parseDdayLink, parseDriveLink, type DdayLink } from "../field/link";
import { prefs } from "../platform/browser";
import { useApp, useSyncStatus } from "../ui/context";
import { Banner, Button, Field } from "../ui/components";

/** The per-assembly link (?a=HAR_01) is remembered, so the installed app (opened without it) still knows. */
export function rememberedLink(): { assemblyId: string; name: string | null } | null {
  const fromUrl = parseAssemblyLink(location.search);
  if (fromUrl) {
    prefs.set("link", JSON.stringify(fromUrl));
    return fromUrl;
  }
  try {
    const v = prefs.get("link");
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

/** The polling-day link's key (and booth), remembered per assembly so the installed app still has it. */
function rememberedDday(aid: string | null): DdayLink | null {
  const fromUrl = parseDdayLink(location.search);
  if (fromUrl && aid) {
    prefs.set(`dday:${aid}`, JSON.stringify(fromUrl));
    return fromUrl;
  }
  try {
    const v = aid ? prefs.get(`dday:${aid}`) : null;
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

/** `?dday=1` (the war room's worker link) opens the polling-day login; remembered for the installed app. */
type LoginMode = "surveyor" | "worker" | "driver";
function rememberedMode(): LoginMode {
  const q = new URLSearchParams(location.search);
  if (q.get("dday") === "1") {
    prefs.set("loginMode", "worker");
    return "worker";
  }
  const m = prefs.get("loginMode");
  return m === "worker" || m === "driver" ? m : "surveyor";
}

export function LoginScreen() {
  const { t } = useApp();
  const [mode, setModeState] = useState(rememberedMode);
  // the war room's polling-day link opens straight into SOS: no choice, no form
  const [direct] = useState(() => new URLSearchParams(location.search).get("dday") === "1" && !!parseDdayLink(location.search));
  // a vehicle's driver link opens straight into the duty screen
  const [drive] = useState(() => rememberedDrive());
  const setMode = (m: LoginMode) => {
    prefs.set("loginMode", m);
    setModeState(m);
  };
  if (drive) return <DriverLogin link={drive} />;
  if (direct) return <WorkerLogin />;
  return (
    <div className="stack">
      <img className="login-logo" src="/brand/npac-logo.webp" alt="N-PAC Pvt. Ltd. — आपकी राजनीतिक सफलता की कुंजी" width={1400} height={411} />
      <div className="chips login-mode" role="radiogroup" aria-label={t.ddayTitle}>
        <button type="button" role="radio" aria-checked={mode === "surveyor"} className={`chip${mode === "surveyor" ? " chip-on" : ""}`} onClick={() => setMode("surveyor")} data-testid="mode-surveyor">
          {t.modeSurveyor}
        </button>
        <button type="button" role="radio" aria-checked={mode === "worker"} className={`chip${mode === "worker" ? " chip-on" : ""}`} onClick={() => setMode("worker")} data-testid="mode-worker">
          🚨 {t.modeWorker}
        </button>
        <button type="button" role="radio" aria-checked={mode === "driver"} className={`chip${mode === "driver" ? " chip-on" : ""}`} onClick={() => setMode("driver")} data-testid="mode-driver">
          🚐 {t.modeDriver}
        </button>
      </div>
      {mode === "worker" ? <WorkerLogin /> : mode === "driver" ? <DriverPaste /> : <SurveyorLogin />}
    </div>
  );
}

/**
 * The vehicle's driver pastes the link the system made (N-PAC, 4 Nov 2026: "ड्राइवर के लिए — केवल सिस्टम से मिले
 * लिंक को डाल देगा, सेव करने से ही काम शुरू"): the app reopens itself with that link and signs in by itself.
 */
function DriverPaste() {
  const { t } = useApp();
  const [text, setText] = useState("");
  const [bad, setBad] = useState(false);
  const open = () => {
    const s = text.trim();
    const q = s.includes("?") ? s.slice(s.indexOf("?")).split("#")[0] : "";
    if (!parseAssemblyLink(q) || !parseDriveLink(q)) return setBad(true);
    location.assign(`${location.origin}${location.pathname}${q}`);
  };
  return (
    <div className="stack" data-testid="driver-paste">
      <div className="big-title">🚐 {t.drvPasteTitle}</div>
      <p className="muted" style={{ margin: 0 }}>
        {t.drvPasteHow}
      </p>
      <div className="card">
        <Field
          label={t.drvPasteLabel}
          name="drive-link"
          value={text}
          onChange={(v) => {
            setText(v);
            setBad(false);
          }}
          placeholder="https://…/?a=HAR_01&drive=…"
          onEnter={open}
          error={bad ? t.drvPasteBad : undefined}
        />
        <Button onClick={open} disabled={!text.trim()} testId="drive-paste-open">
          {t.drvPasteOpen}
        </Button>
      </div>
    </div>
  );
}

/** The driver link (`?a=…&drive=<key>`), remembered so the installed app opens the duty screen again. */
function rememberedDrive(): { assemblyId: string; key: string } | null {
  const a = parseAssemblyLink(location.search);
  const key = parseDriveLink(location.search);
  if (a && key) {
    prefs.set("drive", JSON.stringify({ assemblyId: a.assemblyId, key }));
    return { assemblyId: a.assemblyId, key };
  }
  if (a || new URLSearchParams(location.search).get("dday")) return null; // another link was opened
  try {
    const v = prefs.get("drive");
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

/** Vehicle's driver: the link's key is the only credential; the app signs in by itself. */
function DriverLogin({ link }: { link: { assemblyId: string; key: string } }) {
  const { app, t, lang } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    let off = false;
    setError(null);
    app.driveLogin(link.assemblyId, link.key).catch((e) => {
      if (off) return;
      const code = (e as { code?: string })?.code;
      const kind = classifyLoginError(e);
      if (code === "drive_closed") prefs.set("drive", null);
      setError(kind === "offline" ? t.loginOffline : code === "drive_closed" ? t.drvClosed : kind === "too_many" ? t.loginTooMany : apiErrorText(e, lang) ?? t.err_generic);
    });
    return () => {
      off = true;
    };
  }, [app, link, tries, lang, t]);
  return (
    <div className="stack" data-testid="drive-login">
      <div className="big-title">🚐 {t.drvOpening}</div>
      {error ? (
        <Banner kind="error" action={{ label: t.reload, onClick: () => setTries((n) => n + 1) }}>
          {error}
        </Banner>
      ) : (
        <div className="opening" aria-hidden />
      )}
    </div>
  );
}

/**
 * Opened without the war room's link (N-PAC, 31 Oct 2026: "ये नहीं खुल रहा"): say where the link comes from,
 * let a link copied from WhatsApp be pasted here, and point surveyors / admins to their own login.
 */
function NoDdayLink() {
  const { t } = useApp();
  const [text, setText] = useState("");
  const [bad, setBad] = useState(false);
  const open = () => {
    const s = text.trim();
    const q = s.includes("?") ? s.slice(s.indexOf("?")).split("#")[0] : "";
    if (!parseAssemblyLink(q) || !parseDdayLink(q) || new URLSearchParams(q).get("dday") !== "1") return setBad(true);
    // the same app, opened with the link's own address: it signs in by itself
    location.assign(`${location.origin}${location.pathname}${q}`);
  };
  return (
    <div className="stack" data-testid="dday-login">
      <div className="big-title">🚨 {t.ddayTitle}</div>
      <Banner kind="info" testId="dday-no-link">
        {t.ddayNoLink}
      </Banner>
      <p className="muted" style={{ margin: 0 }}>
        {t.ddayHow}
      </p>
      <div className="card">
        <Field
          label={t.ddayPasteLabel}
          name="dday-link"
          value={text}
          onChange={(v) => {
            setText(v);
            setBad(false);
          }}
          placeholder="https://…/?a=HAR_01&dday=1&k=…"
          onEnter={open}
          error={bad ? t.ddayPasteBad : undefined}
        />
        <Button onClick={open} disabled={!text.trim()} testId="dday-paste-open">
          {t.ddayPasteOpen}
        </Button>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        {t.ddaySurveyorHint}
      </p>
    </div>
  );
}

/**
 * Polling-day worker / agent (N-PAC, 28 Oct 2026: "SOS में कोई लॉगिन नहीं; लिंक पर क्लिक करते ही खुले,
 * SOS कोई भी भेज सके"): the war room's link carries the assembly and a key, so the app signs the
 * phone in by itself as soon as the link opens and shows the SOS screen. No form.
 */
function WorkerLogin() {
  const { app, t, lang } = useApp();
  const [link] = useState(rememberedLink);
  const aid = link?.assemblyId ?? null;
  const [dday] = useState(() => rememberedDday(aid));
  const [error, setError] = useState<string | null>(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!aid || !dday) return;
    let off = false;
    setError(null);
    app.ddayLogin(aid, dday.key, dday.booth).catch((e) => {
      if (off) return;
      const code = (e as { code?: string })?.code;
      const kind = classifyLoginError(e);
      setError(
        kind === "offline" ? t.loginOffline
        : code === "dday_closed" ? t.ddayClosed
        : kind === "too_many" ? t.loginTooMany
        : apiErrorText(e, lang) ?? `${t.err_generic}: ${e instanceof Error ? e.message : String(e)}`
      );
    });
    return () => {
      off = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, aid, dday, tries]);

  if (!aid || !dday) return <NoDdayLink />;
  return (
    <div className="stack" data-testid="dday-login">
      <div className="big-title">🚨 {t.ddayOpening}</div>
      {error ? (
        <Banner kind="error" testId="login-error" action={{ label: t.ddayRetry, onClick: () => setTries((n) => n + 1) }}>
          {error}
        </Banner>
      ) : (
        <div className="opening" role="status">
          <span className="spinner" aria-hidden /> {t.ddayOpeningSub}
        </div>
      )}
    </div>
  );
}

function SurveyorLogin() {
  const { app, t, lang } = useApp();
  const sync = useSyncStatus();
  const [login, setLogin] = useState(app.user?.email ?? app.user?.phone ?? "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState<null | "login" | "booths">(null);
  const [error, setError] = useState<string | null>(null);
  // the link's assembly is only a hint for admins with several assemblies; the login decides
  const [link] = useState(rememberedLink);
  // an admin's second step (N-PAC, 4 Nov 2026): the code on N-PAC's / the candidate's number
  const [otp, setOtp] = useState<OtpStep | null>(null);
  const [code, setCode] = useState("");
  const [wait, setWait] = useState(0);
  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  const fail = (e: unknown) => {
    const kind = classifyLoginError(e);
    const own =
      kind === "offline" ? t.loginOffline
      : kind === "disabled" ? t.loginDisabled
      : kind === "too_many" ? t.loginTooMany
      : kind === "invalid" ? t.loginInvalid
      : kind === "wrong" ? t.loginWrong
      : `${t.err_generic}: ${e instanceof Error ? e.message : String(e)}`;
    setError(kind === "offline" ? own : apiErrorText(e, lang) ?? own);
  };

  const submitOtp = async () => {
    if (!otp || code.replace(/\D/g, "").length < 4) return;
    setError(null);
    setBusy("login");
    try {
      await app.loginOtp(otp.challengeId, code.replace(/\D/g, ""), link?.assemblyId ?? null, setBusy);
      setPassword("");
      setOtp(null);
      setCode("");
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 410 || status === 429) {
        setOtp(null);
        setCode("");
      } else setCode("");
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    if (!otp) return;
    setError(null);
    try {
      setOtp({ ...otp, ...(await app.api.otpResend(otp.challengeId)) });
      setWait(30);
    } catch (e) {
      fail(e);
    }
  };

  const submit = async () => {
    if (!login.trim() || !password) return;
    setError(null);
    setBusy("login");
    try {
      const r = await app.login(login, password, link?.assemblyId ?? null, setBusy);
      if (r.otp) {
        setOtp(r.otp);
        setCode("");
        setWait(30);
        return;
      }
      setPassword("");
    } catch (e) {
      // the API's own words in the app's language (messageHi / message), else ours
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  if (otp) {
    const channel = otp.channel === "whatsapp" ? "WhatsApp" : otp.channel === "sms" ? "SMS" : t.otpDev;
    return (
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submitOtp();
        }}
        data-testid="login-otp"
      >
        <div>
          <div className="big-title">🔐 {t.otpTitle}</div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{otp.user.name}</div>
        </div>
        <p>{t.otpSentTo(otp.to, channel)}</p>
        <Field label={t.otpCode} name="otp" value={code} onChange={(v) => setCode(v.replace(/\D/g, ""))} inputMode="numeric" autoComplete="one-time-code" hint={otp.devCode ? `${t.otpDevHint} ${otp.devCode}` : undefined} />
        {error ? (
          <Banner kind="error" testId="login-error">
            {error}
          </Banner>
        ) : null}
        <Button type="submit" big busy={!!busy} disabled={code.length < 4} testId="otp-submit">
          {busy ? t.loggingIn : t.otpVerify}
        </Button>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <Button variant="ghost" disabled={wait > 0} onClick={() => void resend()} testId="otp-resend">
            {wait > 0 ? t.otpResendIn(wait) : t.otpResend}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setOtp(null);
              setCode("");
              setError(null);
            }}
          >
            {t.cancel}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      data-testid="login"
    >
      <div>
        <div className="big-title">{t.appName}</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{t.loginSubtitle}</div>
      </div>
      <p className="muted" style={{ margin: 0 }} data-testid="login-auto">
        {t.loginAutoAssembly}
      </p>
      {app.authError?.code === "account_disabled" ? (
        <Banner kind="error" testId="login-session">
          {apiErrorText(app.authError, lang) ?? t.loginDisabled}
        </Banner>
      ) : app.sessionExpired ? (
        <Banner kind="warn" testId="login-session">
          {t.sessionExpired}
        </Banner>
      ) : null}
      {sync.counts.total > 0 ? <Banner kind="info">{t.unsentOnPhone(sync.counts.total)}</Banner> : null}
      <div>
        <Field label={t.loginId} name="login" value={login} onChange={setLogin} hint={t.loginIdHint} autoComplete="username" inputMode="email" />
        <Field label={t.password} name="password" type={show ? "text" : "password"} value={password} onChange={setPassword} autoComplete="current-password" />
        <Button variant="ghost" onClick={() => setShow(!show)}>
          {show ? t.hidePassword : t.showPassword}
        </Button>
      </div>
      {error ? (
        <Banner kind="error" testId="login-error">
          {error}
        </Banner>
      ) : null}
      <Button type="submit" big busy={!!busy} disabled={!login.trim() || !password} testId="login-submit">
        {busy === "booths" ? t.downloadingBooths : busy ? t.loggingIn : t.loginButton}
      </Button>
      <p className="muted">{t.sessionInfo}</p>
      <p className="muted">
        {t.serverLabel}: {app.api.baseUrl}
      </p>
    </form>
  );
}
